import { InlineLoading, Tag } from '@carbon/react';
import { FormattedCount } from 'components/formatted-count';
import type { CaptureSummary } from 'types/dps-capture';
import { WELL_STATUS_COUNT_COLUMNS } from 'utils/slot-categories';
import { ExclusionBanner } from './exclusion-banner';
import styles from './capture.module.scss';

interface CaptureSummaryPanelProps {
  /** The positional summary for this area, or null while no geometry is set. */
  summary: CaptureSummary | null;
  loading: boolean;
  /** True once a geometry is assigned (drives the empty-vs-prompt copy). */
  hasGeometry: boolean;
  /** True for a slot-targeted area, so a zero-capture reads as not-in-universe. */
  isSlotTarget?: boolean;
  onWiden?: () => void;
}

/**
 * Per-area capture panel (mock 1.4): summary chips + date buckets + the
 * three-population banner, from the positional summary for that area. Renders a
 * prompt for a geometry-less panel and an empty state for a geometry with no
 * hits.
 *
 * @param {CaptureSummaryPanelProps} props - The summary, load state, and geometry flag.
 * @return {JSX.Element} The panel content.
 */
export function CaptureSummaryPanel({
  summary,
  loading,
  hasGeometry,
  isSlotTarget = false,
  onWiden,
}: CaptureSummaryPanelProps): JSX.Element {
  if (!hasGeometry) {
    return (
      <p className={styles.prompt}>
        No intersecting slots yet — choose a geometry type and area
      </p>
    );
  }

  if (loading && !summary) {
    return <InlineLoading description="Capturing…" />;
  }

  if (!summary || summary.intersectingSlotCount === 0) {
    // A slot always targets exactly one slot, so zero captured means the target
    // is no longer paceable under the current selection (the stale state, 5.3) —
    // distinct from a polygon's no-hits empty state.
    if (isSlotTarget) {
      return (
        <p className={styles.prompt}>
          This slot is not in the selected Type Curve Scenario&apos;s paceable
          inventory - it will not be scheduled
        </p>
      );
    }
    return (
      <p className={styles.prompt}>No intersecting slots for this geometry</p>
    );
  }

  return (
    <div className={styles.panel} aria-busy={loading}>
      <div className={styles.chips}>
        <Tag type="blue">
          <FormattedCount value={summary.intersectingSlotCount} /> intersecting
          slots
        </Tag>
        <Tag type="teal">
          LTD <FormattedCount value={summary.byCategory.ltd} />
        </Tag>
        <Tag type="green">
          Permit <FormattedCount value={summary.byCategory.permit} />
        </Tag>
        <Tag type="purple">
          DUC <FormattedCount value={summary.byCategory.duc} />
        </Tag>
        {WELL_STATUS_COUNT_COLUMNS.map(({ key, label }) => (
          <Tag key={key} type="magenta">
            {label} <FormattedCount value={summary.byCategory[key] ?? 0} />
          </Tag>
        ))}
        <Tag type="cool-gray">
          <FormattedCount value={summary.unitCount} /> units
        </Tag>
        {summary.alreadyCoveredCount > 0 && (
          <Tag type="warm-gray">
            <FormattedCount value={summary.alreadyCoveredCount} /> already
            covered
          </Tag>
        )}
      </div>

      <div className={styles.chips}>
        <span className={styles.sectionLabel}>Dates</span>
        <Tag type="gray">
          <FormattedCount value={summary.dateBuckets.onFile} /> on file
        </Tag>
        <Tag type="gray">
          <FormattedCount value={summary.dateBuckets.scheduledHere} /> scheduled
          here
        </Tag>
        <Tag type="gray">
          <FormattedCount value={summary.dateBuckets.pacedFromReferenceDate} />{' '}
          paced from reference date
        </Tag>
      </div>

      <ExclusionBanner exclusions={summary.exclusions} onWiden={onWiden} />
    </div>
  );
}
